/**
 * AppsMarketplaceSection — the App Marketplace rendered as a section
 * inside MarketplacePage. No outer `<div className="marketplace-page">`
 * wrapper; the parent owns that. Use this when you want the apps tiles
 * to sit alongside / below the workflow tiles on the same page.
 *
 * For the standalone deep-link `/marketplace/apps`, AppsMarketplacePage
 * wraps this same component with its own page wrapper.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { rocketOutline, openOutline, alertCircle, star, starOutline } from 'ionicons/icons';

import { Apps } from '../../services/apps';
import ProjectsService from '../../services/projects';
import authService from '../../services/auth';
import '../MarketplacePage/MarketplaceDetailModal.css';
import './AppsMarketplacePage.css';

/**
 * @param {Object} props
 * @param {boolean} [props.showHeader=true]  Render the section's own h2 +
 *   subtitle. Pass false when the parent page already provides the
 *   heading (e.g. the standalone /marketplace/apps wrapper).
 */
const AppsMarketplaceSection = ({ showHeader = true }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Deploy modal state
  const [active, setActive] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [pickedProject, setPickedProject] = useState('');
  const [instanceName, setInstanceName] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState(null);

  const { projectId: projectScopedId } = useParams();
  const navigate = useNavigate();
  const isAuthed = authService.isAuthenticated?.() ?? false;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await Apps.listCatalog();
        if (cancelled) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load apps catalog:', err);
        setError(err.message || 'Failed to load catalog');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // When the URL is project-scoped (/projects/:id/marketplace) we skip
  // the project-picker entirely — the user already chose. We still
  // fetch the project NAME so the modal can show which project the
  // instance lands in. Outside a project, we fetch the full list so
  // the user can pick.
  const ensureProjects = useCallback(async () => {
    if (projectScopedId) {
      // Already inside a project — pin to that id and resolve its name
      // for display. A single-project fetch would be cheaper but the
      // shared service only exposes a list call; the result is cached.
      if (pickedProject === projectScopedId && projects.length > 0) return;
      setProjectsLoading(true);
      try {
        const res = await ProjectsService.getProjects();
        const list = res?.projects || [];
        setProjects(list);
        setPickedProject(projectScopedId);
      } catch (err) {
        console.error('Failed to resolve project name:', err);
        setPickedProject(projectScopedId);
      } finally {
        setProjectsLoading(false);
      }
      return;
    }
    if (projects.length > 0 || projectsLoading) return;
    setProjectsLoading(true);
    try {
      const res = await ProjectsService.getProjects();
      const list = res?.projects || [];
      setProjects(list);
      setPickedProject(list[0]?.id || '');
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setProjectsLoading(false);
    }
  }, [projects.length, projectsLoading, projectScopedId, pickedProject]);

  // Project name lookup for the "deploying to" line shown when the
  // picker is hidden (project-scoped route).
  const pickedProjectName = React.useMemo(() => {
    const p = projects.find((x) => x.id === pickedProject);
    return p?.name || '';
  }, [projects, pickedProject]);

  const openDeploy = useCallback((entry) => {
    if (!isAuthed) {
      navigate(`/signup?next=${encodeURIComponent('/marketplace')}`);
      return;
    }
    setActive(entry);
    setInstanceName('');
    setDeployError(null);
    ensureProjects();
  }, [isAuthed, navigate, ensureProjects]);

  const closeDeploy = () => {
    if (deploying) return;
    setActive(null);
    setDeployError(null);
  };

  const handleDeploy = async () => {
    if (!active || !pickedProject) return;
    setDeploying(true);
    setDeployError(null);
    try {
      const res = await Apps.deploy({
        appType: active.appType,
        projectId: pickedProject,
        name: instanceName || undefined,
      });
      const instanceId = res?.instanceId;
      if (instanceId) {
        navigate(`/projects/${pickedProject}/apps/${instanceId}`);
      } else {
        closeDeploy();
      }
    } catch (err) {
      console.error('Deploy failed:', err);
      setDeployError(err.message || 'Deploy failed');
      setDeploying(false);
    }
  };

  // Render nothing (not even the section heading) while loading or
  // when the catalog endpoint is unreachable. Local dev that hasn't
  // restarted devup.sh after pulling the catalog code will 404 on
  // GET /apps/catalog — better to hide the whole section than show a
  // scary "Not Found" panel beneath the workflow marketplace.
  if (loading || error || items.length === 0) return null;

  return (
    <>
      <section className="marketplace-section apps-section">
        {showHeader && (
          <header className="apps-section-header">
            <h2 className="apps-section-title">App Marketplace</h2>
            <p className="apps-section-subtitle">
              One-click hosted instances of popular open-source apps. Each
              instance is private to you, with an AI ops agent for install,
              monitoring, and upgrades.
            </p>
          </header>
        )}

        <ol className="marketplace-grid">
          {items.map((entry, idx) => (
            <li key={entry.appType} className="marketplace-card-li">
              <button
                type="button"
                className="marketplace-card"
                onClick={() => openDeploy(entry)}
                aria-label={`Deploy ${entry.displayName}`}
              >
                <span className="marketplace-rank">{idx + 1}</span>
                <span className="marketplace-card-icon-wrap">
                  {entry.icon ? (
                    // eslint-disable-next-line jsx-a11y/img-redundant-alt
                    <img
                      src={entry.icon}
                      alt=""
                      className="marketplace-card-icon"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="marketplace-card-icon marketplace-card-icon--fallback">
                      {(entry.displayName || entry.appType).slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </span>
                <span className="marketplace-card-body">
                  <span className="marketplace-card-name">
                    {entry.displayName}
                    <span className="apps-card-version">v{entry.appVersion}</span>
                  </span>
                  <span className="marketplace-card-tagline">{entry.tagline}</span>
                  <span className="marketplace-card-author">
                    By Zibby
                    <span className="marketplace-card-author-badge">Official</span>
                    <span className="apps-card-meta">{entry.category}</span>
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      </section>

      {active && (
        <div className="mkt-modal-overlay" onClick={closeDeploy} role="presentation">
          <div
            className="mkt-modal"
            role="dialog"
            aria-label={`Deploy ${active.displayName}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="mkt-modal-close"
              onClick={closeDeploy}
              aria-label="Close"
              disabled={deploying}
            >×</button>

            <div className="mkt-modal-body">
              {/* Reuse the workflow-detail-modal header shape (icon
                  centered on top, title centered below) so the apps
                  modal looks like a sibling of the workflow modal. */}
              <header className="mkt-modal-header">
                {active.icon ? (
                  // eslint-disable-next-line jsx-a11y/img-redundant-alt
                  <img src={active.icon} alt="" className="mkt-modal-icon" />
                ) : (
                  <div className="mkt-modal-icon mkt-modal-icon--fallback">
                    {(active.displayName || active.appType).slice(0, 1).toUpperCase()}
                  </div>
                )}
                <h2 className="mkt-modal-title">{active.displayName}</h2>
                <p className="mkt-modal-author">
                  By Zibby
                  <span className="mkt-modal-author-badge">Official</span>
                </p>
                <p className="mkt-modal-tagline">{active.tagline}</p>

                {/* Stats row matches the workflow modal: rating + stars
                    + category. Ratings backend for apps isn't live yet
                    (v0.3 sprint) — render the placeholder so the
                    visual layout matches and we just swap in real
                    numbers later without touching the JSX shape. */}
                <div className="mkt-modal-stats" aria-label="Stats">
                  <div className="mkt-modal-stat">
                    <div className="mkt-modal-stat-value">
                      <span className="mkt-modal-stat-number">0.0</span>
                      <span className="mkt-modal-stat-stars" aria-hidden="true">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <IonIcon key={n} icon={starOutline} />
                        ))}
                      </span>
                    </div>
                    <div className="mkt-modal-stat-label">0 RATINGS</div>
                  </div>
                  <div className="mkt-modal-stat">
                    <div className="mkt-modal-stat-value mkt-modal-stat-category">
                      {active.category || '—'}
                    </div>
                    <div className="mkt-modal-stat-label">Category</div>
                  </div>
                  <div className="mkt-modal-stat">
                    <div className="mkt-modal-stat-value">v{active.appVersion}</div>
                    <div className="mkt-modal-stat-label">Version</div>
                  </div>
                </div>
              </header>

              {active.ui?.setupHint && (
                <p className="apps-modal-hint">
                  <strong>Heads up:</strong> {active.ui.setupHint}
                </p>
              )}

              <ul className="apps-modal-facts">
                <li><strong>Public URL</strong> <span>https://<em>{'<id>'}</em>.apps.zibby.dev</span></li>
                <li><strong>Resources</strong> <span>{(active.resources.cpu / 1024).toFixed(2)} vCPU · {(active.resources.memory / 1024).toFixed(2)} GB RAM</span></li>
                <li><strong>Cost</strong> <span>$29 / month (added to your Zibby subscription)</span></li>
                {active.homepage && (
                  <li>
                    <strong>Homepage</strong>
                    <span>
                      <a
                        href={active.homepage}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="apps-modal-homepage-link"
                      >
                        <IonIcon icon={openOutline} aria-hidden="true" />
                        {active.homepage.replace(/^https?:\/\//, '')}
                      </a>
                    </span>
                  </li>
                )}
              </ul>

              {/* RATINGS section placeholder — matches workflow modal
                  layout so the page reads as a sibling. Real ratings
                  hook into the same JSX shape in v0.3. */}
              <section className="mkt-modal-section apps-modal-ratings-section">
                <h3 className="mkt-modal-section-title">RATINGS</h3>
                <p className="mkt-modal-no-ratings">No ratings yet.</p>
              </section>

              {deployError && (
                <div className="apps-modal-error">
                  <IonIcon icon={alertCircle} aria-hidden="true" /> {deployError}
                </div>
              )}
            </div>

            {/* Footer mirrors the workflow Deploy footer's shape:
                label row + input/control row. Apps have no agent
                picker (the daemon is fixed) and no warm-pool (one
                instance per row, not Fargate-scaled), so the controls
                here are just: project picker (when not URL-scoped) OR
                read-only "Deploying to" line + instance-name field +
                Deploy button. Same `mkt-modal-name-*` classes as the
                workflow footer so the styling rides along for free. */}
            <footer className="mkt-modal-footer apps-modal-footer">
              <div className="mkt-modal-name-label-row">
                <label className="mkt-modal-name-label" htmlFor="apps-deploy-name-input">
                  Instance name
                </label>
                <p className="mkt-modal-name-help is-valid" aria-live="polite">
                  {pickedProjectName
                    ? `Available — will deploy to ${pickedProjectName}`
                    : (projectsLoading ? 'Loading project…' : '')}
                </p>
              </div>
              <div className="mkt-modal-name-row is-valid">
                {!projectScopedId && (
                  <select
                    className="mkt-field mkt-field--select apps-modal-footer-project"
                    value={pickedProject}
                    onChange={(e) => setPickedProject(e.target.value)}
                    disabled={projectsLoading || deploying}
                    aria-label="Project for this instance"
                  >
                    {projectsLoading && <option value="">Loading…</option>}
                    {!projectsLoading && projects.length === 0 && (
                      <option value="">No projects</option>
                    )}
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                )}
                <input
                  id="apps-deploy-name-input"
                  type="text"
                  className="mkt-modal-name-input"
                  value={instanceName}
                  onChange={(e) => setInstanceName(e.target.value.slice(0, 80))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && pickedProject && !deploying) {
                      e.preventDefault();
                      handleDeploy();
                    }
                  }}
                  placeholder={active.displayName}
                  autoComplete="off"
                  spellCheck="false"
                  disabled={deploying}
                />
                <button
                  type="button"
                  className="mkt-modal-deploy"
                  disabled={!pickedProject || deploying}
                  onClick={handleDeploy}
                >
                  <IonIcon icon={rocketOutline} aria-hidden="true" />
                  {deploying ? 'Deploying…' : 'Deploy'}
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

export default AppsMarketplaceSection;
